import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { Button } from '@revelio/ui';

import { useToast } from '../../../hooks/useToast';
import modalStyles from '../../Modal/themedModal.module.css';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import { ParameterEditor } from './ParameterEditor';
import { determinePrimaryParameter, parametersAreValid } from './parameters';

export function ReportParametersModal({
  isOpen,
  onClose,
  defaultValue,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultValue?: ReportParameterConfig[];
  onSave: (params: ReportParameterConfig[] | undefined) => void;
}) {
  const toast = useToast();
  const [parameters, setParameters] = useState<ReportParameterConfig[]>(
    defaultValue ?? []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setParameters(defaultValue ?? []);
  }, [defaultValue, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent className={modalStyles.content}>
        <ModalHeader color="text.primary">
          Configure Report Parameters
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={6}>
            <ParameterEditor parameters={parameters} onChange={setParameters} />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            size="sm"
            color="secondary"
            variant="ghost"
            onClick={onClose}
            style={{ marginRight: '0.5rem' }}
          >
            Close
          </Button>
          <Button
            size="sm"
            color="primary"
            style={{ marginRight: '0.5rem' }}
            onClick={() => {
              const parametersWithPrimary =
                determinePrimaryParameter(parameters);
              const validation = parametersAreValid(parametersWithPrimary);
              if (validation.isValid) {
                onSave(parametersWithPrimary);
                onClose();
              } else {
                toast({
                  title: 'Invalid parameter configuration',
                  description: validation.reason,
                  status: 'error',
                });
              }
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
